import React, { memo } from "react";
import { Workout, WorkoutLog, ExerciseResult } from "@/schemas/workout";
import LoadingWorkout from "./states/LoadingWorkout";
import ErrorWorkout from "./states/ErrorWorkout";
import ReadyWorkout from "./states/ReadyWorkout";
import ActiveWorkoutView from "./states/ActiveWorkout";
import CompleteWorkout from "./states/CompleteWorkout";
import { WorkoutState } from "@/hooks/useActiveWorkoutSession";

interface WorkoutStateManagerProps {
  workoutState: WorkoutState;
  workout: Workout | null;
  workoutLog: WorkoutLog | null;
  exerciseResults: Record<string, ExerciseResult>;
  elapsedTime: number;
  onStartWorkout: () => void;
  onCompleteWorkout: () => void;
  onNavigateBack: () => void;

  // Active workout props
  currentExerciseIndex: number;
  currentSetIndex: number;
  restMode: boolean;
  currentRestTime: number;
  workoutProgress: number;
  isLastExercise: boolean;
  isLastSet: boolean;
  totalSets: number;

  // Sound props
  soundEnabled: boolean;
  toggleSound: () => void;
  playSound: (type: "timer" | "rest" | "countdown" | "complete") => void;

  // Voice props
  voiceEnabled: boolean;
  toggleVoice: () => void;

  // Workout actions
  completeSet: () => void;
  skipExercise: () => void;
  skipRestCommand: () => void;
  handleRestComplete: () => void;
  resetWorkout: () => void;
  exitWorkout: () => void;

  // Debug props
  debugMode: boolean;
  debugVolume: number;
  setDebugVolume: (volume: number) => void;

  // Voice recognition props
  isListening: boolean;
  transcript: string;
  voiceTranscripts: string[];
  voiceStatus: string;
  toggleListening: () => void;
  permissionDenied: boolean;
  recognizedCommand: string;
  showCommandFeedback: boolean;
  setShowCommandFeedback: (show: boolean) => void;
  commandMapping: Record<string, { phrases: string[]; action: () => void; description: string }>;
  isRestAfterExercise: boolean;

  // Pause state and handlers
  isPaused: boolean;
  pauseWorkout: () => void;
  resumeWorkout: () => void;
}

// Use React.memo to prevent unnecessary re-renders
const WorkoutStateManager = memo(function WorkoutStateManager({
  workoutState,
  workout,
  workoutLog,
  exerciseResults,
  elapsedTime,
  onStartWorkout,
  onCompleteWorkout,
  onNavigateBack,
  currentExerciseIndex,
  currentSetIndex,
  restMode,
  currentRestTime,
  workoutProgress,
  isLastExercise,
  isLastSet,
  totalSets,
  soundEnabled,
  toggleSound,
  playSound,
  voiceEnabled,
  toggleVoice,
  completeSet,
  skipExercise,
  skipRestCommand,
  handleRestComplete,
  resetWorkout,
  exitWorkout,
  debugMode,
  debugVolume,
  setDebugVolume,
  isListening,
  transcript,
  voiceTranscripts,
  voiceStatus,
  toggleListening,
  permissionDenied,
  recognizedCommand,
  showCommandFeedback,
  setShowCommandFeedback,
  commandMapping,
  isRestAfterExercise,
  isPaused,
  pauseWorkout,
  resumeWorkout,
}: WorkoutStateManagerProps) {
  // Render the appropriate component based on workout state
  switch (workoutState) {
    case "loading":
      return <LoadingWorkout />;

    case "error":
      return <ErrorWorkout onNavigateBack={onNavigateBack} />;

    case "ready":
      return <ReadyWorkout workout={workout} onStartWorkout={onStartWorkout} />;

    case "active":
      return (
        <ActiveWorkoutView
          workout={workout}
          currentExerciseIndex={currentExerciseIndex}
          currentSetIndex={currentSetIndex}
          restMode={restMode}
          currentRestTime={currentRestTime}
          workoutProgress={workoutProgress}
          isLastExercise={isLastExercise}
          isLastSet={isLastSet}
          totalSets={totalSets}
          soundEnabled={soundEnabled}
          toggleSound={toggleSound}
          playSound={playSound}
          voiceEnabled={voiceEnabled}
          toggleVoice={toggleVoice}
          completeSet={completeSet}
          skipExercise={skipExercise}
          skipRestCommand={skipRestCommand}
          handleRestComplete={handleRestComplete}
          resetWorkout={resetWorkout}
          exitWorkout={exitWorkout}
          debugMode={debugMode}
          debugVolume={debugVolume}
          setDebugVolume={setDebugVolume}
          isListening={isListening}
          transcript={transcript}
          voiceTranscripts={voiceTranscripts}
          voiceStatus={voiceStatus}
          toggleListening={toggleListening}
          permissionDenied={permissionDenied}
          recognizedCommand={recognizedCommand}
          showCommandFeedback={showCommandFeedback}
          setShowCommandFeedback={setShowCommandFeedback}
          commandMapping={commandMapping}
          elapsedTime={elapsedTime}
          isRestAfterExercise={isRestAfterExercise}
          isPaused={isPaused}
          pauseWorkout={pauseWorkout}
          resumeWorkout={resumeWorkout}
        />
      );

    case "complete":
      return (
        <CompleteWorkout
          workout={workout}
          exerciseResults={exerciseResults}
          elapsedTime={elapsedTime}
          onCompleteWorkout={onCompleteWorkout}
        />
      );

    default:
      return <ErrorWorkout onNavigateBack={onNavigateBack} />;
  }
});

export default WorkoutStateManager;
